import { useEffect, useRef, useState } from 'react'
import { Modal, Input, Collapse } from 'react-daisyui'
import { Button } from '@/components/Button.tsx'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { IconUpload } from '@tabler/icons-react'
import WatermarkPreview from '@/components/portal/WatermarkPreview.tsx'
import { useClient } from '@/stores/useClient.ts'
import { HeyDriveWatermark } from '@/models/heyDriveWatermark.ts'
import useCollapses from '@/hooks/useCollapses.tsx'

interface WatermarkEditorModalProps {
  modalRef: React.RefObject<HTMLDialogElement>
  watermark: HeyDriveWatermark | null
  driveId: string
  projectId: string
  isFirstWatermark: boolean
  isActive: boolean
  onSaved?: (wm: HeyDriveWatermark) => void
}

const ORIGIN_PRESETS: Array<{ x: number; y: number; label: string }> = [
  { x: 0, y: 0, label: '↖' },
  { x: 0.5, y: 0, label: '↑' },
  { x: 1, y: 0, label: '↗' },
  { x: 0, y: 0.5, label: '←' },
  { x: 0.5, y: 0.5, label: '·' },
  { x: 1, y: 0.5, label: '→' },
  { x: 0, y: 1, label: '↙' },
  { x: 0.5, y: 1, label: '↓' },
  { x: 1, y: 1, label: '↘' },
]

export default function WatermarkEditorModal({
  modalRef,
  watermark,
  driveId,
  projectId,
  isFirstWatermark,
  isActive,
  onSaved,
}: WatermarkEditorModalProps) {
  const { createWatermark, updateWatermark, activateDriveWatermark } = useClient()
  const queryClient = useQueryClient()
  const { collapse } = useCollapses()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [file, setFile] = useState<File | null>(null)
  const [filePreviewUrl, setFilePreviewUrl] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [rotation, setRotation] = useState(0)
  const [sizeRatio, setSizeRatio] = useState(0.75)
  const [posX, setPosX] = useState(0.5)
  const [posY, setPosY] = useState(0.5)
  const [picOriginX, setPicOriginX] = useState(0.5)
  const [picOriginY, setPicOriginY] = useState(0.5)
  const [opacity, setOpacity] = useState(0.75)
  const [advancedOpen, setAdvancedOpen] = useState(false)

  useEffect(() => {
    let cancelled = false
    if (watermark) {
      setName(watermark.name || '')
      setRotation(watermark.rotation)
      setSizeRatio(watermark.size_ratio)
      setPosX(watermark.pos_x)
      setPosY(watermark.pos_y)
      setPicOriginX(watermark.pic_origin_x)
      setPicOriginY(watermark.pic_origin_y)
      setOpacity(watermark.opacity)
      setFile(null)
      setFilePreviewUrl(null)
    } else {
      setName('')
      setRotation(0)
      setSizeRatio(0.75)
      setPosX(0.5)
      setPosY(0.5)
      setPicOriginX(0.5)
      setPicOriginY(0.5)
      setOpacity(0.75)
      setFile(null)
      setFilePreviewUrl(null)
      // Preload default copyright PNG as starting file
      fetch('/default-copyright.png')
        .then((res) => res.blob())
        .then((blob) => {
          if (cancelled) return
          const defaultFile = new File([blob], 'default-copyright.png', { type: 'image/png' })
          setFile(defaultFile)
        })
        .catch((err) => console.error('Default-Wasserzeichen konnte nicht geladen werden:', err))
    }
    setAdvancedOpen(false)
    return () => {
      cancelled = true
    }
  }, [watermark])

  useEffect(() => {
    if (!file) {
      setFilePreviewUrl(null)
      return
    }
    const url = URL.createObjectURL(file)
    setFilePreviewUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [file])

  const advancedCollapse = collapse('advanced')
  const isAdvancedOpen = advancedOpen || advancedCollapse.open

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const selected = e.target.files?.[0]
    if (selected) setFile(selected)
  }

  const effectiveWmUrl = filePreviewUrl ?? watermark?.file ?? null

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (watermark) {
        const payload: any = {
          name,
          rotation,
          size_ratio: sizeRatio,
          pos_x: posX,
          pos_y: posY,
          pic_origin_x: picOriginX,
          pic_origin_y: picOriginY,
          opacity,
        }
        if (file) payload.file = file
        const res = await updateWatermark(watermark.id, payload)
        return res.data as HeyDriveWatermark
      } else {
        if (!file) throw new Error('Datei fehlt')
        const res = await createWatermark({
          file,
          name,
          rotation,
          size_ratio: sizeRatio,
          pos_x: posX,
          pos_y: posY,
          pic_origin_x: picOriginX,
          pic_origin_y: picOriginY,
          opacity,
        })
        return res.data as HeyDriveWatermark
      }
    },
    onSuccess: async (saved) => {
      let nowActive = isActive
      if (!watermark && isFirstWatermark) {
        try {
          await activateDriveWatermark(driveId, saved.id)
          nowActive = true
        } catch (err) {
          console.error('Auto-Aktivierung fehlgeschlagen:', err)
        }
      }
      queryClient.invalidateQueries({ queryKey: ['watermarks'] })
      queryClient.invalidateQueries({ queryKey: ['project', projectId] })
      if (nowActive) {
        toast('Wasserzeichen gespeichert. Bestehende Bilder werden im Hintergrund aktualisiert.', { closeButton: true, autoClose: 4000 })
      } else {
        toast('Wasserzeichen gespeichert', { closeButton: true, autoClose: 2000 })
      }
      onSaved?.(saved)
      modalRef.current?.close()
    },
    onError: (err: any) => {
      console.error('Wasserzeichen speichern fehlgeschlagen:', err)
      toast.error('Speichern fehlgeschlagen')
    },
  })

  const canSave = watermark ? true : !!file
  const isPending = saveMutation.isPending

  return (
    <Modal ref={modalRef} className="bg-neutral max-w-5xl w-full">
      <Modal.Header>
        <span className="text-xl font-bold">{watermark ? 'Wasserzeichen bearbeiten' : 'Neues Wasserzeichen'}</span>
        <form method="dialog">
          <button className="btn btn-sm btn-circle btn-ghost absolute right-5 top-5">✕</button>
        </form>
      </Modal.Header>
      <Modal.Body>
        <div className="flex flex-col md:flex-row gap-6">
          <div className="flex-1 flex flex-col items-center gap-2">
            <WatermarkPreview
              watermarkUrl={effectiveWmUrl}
              posX={posX}
              posY={posY}
              picOriginX={picOriginX}
              picOriginY={picOriginY}
              sizeRatio={sizeRatio}
              rotation={rotation}
              opacity={opacity}
              editable={isAdvancedOpen}
              onPositionChange={(x, y) => {
                setPosX(x)
                setPosY(y)
              }}
            />
            <p className="text-xs opacity-60 text-center">
              {isAdvancedOpen
                ? 'Klicke ins Bild, um die Position zu setzen.'
                : 'Öffne erweiterte Einstellungen, um die Position anzupassen.'}
            </p>
          </div>

          <div className="flex-1 flex flex-col gap-4 min-w-0">
            <div className="flex flex-col gap-2">
              <label className="text-sm font-semibold">Wasserzeichen-Bild (PNG mit transparentem Hintergrund empfohlen)</label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/*"
                className="hidden"
                onChange={handleFileChange}
              />
              <Button
                color="ghost"
                className="border border-[var(--portal-border)] justify-start"
                onClick={() => fileInputRef.current?.click()}
                startIcon={<IconUpload size={18} />}
              >
                {file ? file.name : watermark?.file_name || 'Datei auswählen'}
              </Button>
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-semibold">Name (optional)</label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="z. B. Studio-Logo"
                className="bg-neutral border-[var(--portal-border)]"
              />
            </div>

            <Collapse
              className="collapse-arrow bg-secondary border border-[var(--portal-border)]"
              open={isAdvancedOpen}
            >
              <Collapse.Title
                onClick={() => {
                  setAdvancedOpen(!isAdvancedOpen)
                  advancedCollapse.onClick()
                }}
                className="font-semibold cursor-pointer"
              >
                Erweiterte Einstellungen
              </Collapse.Title>
              <Collapse.Content>
                <div className="flex flex-col gap-4 pt-2">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Deckkraft</span>
                      <span className="opacity-60">{Math.round(opacity * 100)}%</span>
                    </div>
                    <input
                      type="range"
                      className="range range-primary range-sm w-full"
                      min={0} max={1} step={0.05}
                      value={opacity}
                      onChange={(e) => setOpacity(Number(e.target.value))}
                    />
                  </div>

                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Größe</span>
                      <span className="opacity-60">{Math.round(sizeRatio * 100)}%</span>
                    </div>
                    <input
                      type="range"
                      className="range range-primary range-sm w-full"
                      min={0.05} max={1} step={0.05}
                      value={sizeRatio}
                      onChange={(e) => setSizeRatio(Number(e.target.value))}
                    />
                  </div>

                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Rotation</span>
                      <span className="opacity-60">{rotation}°</span>
                    </div>
                    <input
                      type="range"
                      className="range range-primary range-sm w-full"
                      min={-180} max={180} step={1}
                      value={rotation}
                      onChange={(e) => setRotation(Number(e.target.value))}
                    />
                  </div>

                  <div>
                    <div className="text-sm mb-1">Ankerpunkt (welcher Teil des Wasserzeichens an der Position sitzt)</div>
                    <div className="flex items-end gap-3 flex-wrap">
                      <div className="grid grid-cols-3 gap-1 w-fit">
                        {ORIGIN_PRESETS.map((p) => {
                          const selected = Math.abs(p.x - picOriginX) < 0.01 && Math.abs(p.y - picOriginY) < 0.01
                          return (
                            <button
                              key={`${p.x}-${p.y}`}
                              type="button"
                              onClick={() => {
                                setPicOriginX(p.x)
                                setPicOriginY(p.y)
                              }}
                              className={`w-8 h-8 flex items-center justify-center border text-sm ${
                                selected
                                  ? 'border-primary bg-primary text-primary-content'
                                  : 'border-[var(--portal-border)] hover:bg-neutral'
                              }`}
                            >
                              {p.label}
                            </button>
                          )
                        })}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        color="ghost"
                        onClick={() => {
                          setPosX(0.5)
                          setPosY(0.5)
                          setPicOriginX(0.5)
                          setPicOriginY(0.5)
                        }}
                      >
                        Zentrieren
                      </Button>
                    </div>
                  </div>

                  <div className="text-xs opacity-60">Position: {Math.round(posX * 100)}% / {Math.round(posY * 100)}%</div>
                </div>
              </Collapse.Content>
            </Collapse>
          </div>
        </div>
      </Modal.Body>
      <Modal.Actions>
        <form method="dialog">
          <Button color="ghost" disabled={isPending}>Abbrechen</Button>
        </form>
        <Button
          color="primary"
          onClick={() => saveMutation.mutate()}
          loading={isPending}
          disabled={isPending || !canSave}
        >
          Speichern
        </Button>
      </Modal.Actions>
    </Modal>
  )
}
